import { useState, useRef, useMemo } from "react";
import { GetProp } from "antd/lib";
import { Bubble } from "@ant-design/x";
import { SmartCompressor } from "../../../../utils/compressors/SmartCompressor";
import type { ChatStatus } from "../types";

export const useChatState = (csUuid?: string, status?: string) => {
  const [content, setContent] = useState(""); // 输入框内容
  const [messages, setMessages] = useState<
    GetProp<typeof Bubble.List, "items">
  >([]); // 消息列表
  const [previewVisible, setPreviewVisible] = useState(false); // 图片预览模态框
  const [previewImage, setPreviewImage] = useState(""); // 图片预览
  const csUuidRef = useRef(csUuid); // 令牌
  const [imageLoading, setImageLoading] = useState(false); // 图片加载状态
  const [previewBase64, setPreviewBase64] = useState<string | null>(null); // 图片预览Base64
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null); // 上传图片URL
  const uploadedImageUrlRef = useRef<string | null>(null); // 上传图片URL引用
  const [videoLoading, setVideoLoading] = useState(false); // 视频加载状态
  const [previewVideo, setPreviewVideo] = useState<string | null>(null); // 视频预览
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState<string | null>(null); // 上传视频URL
  const uploadedVideoUrlRef = useRef<string | null>(null); // 上传视频URL引用
  const [videoVisible, setVideoVisible] = useState(false); // 视频预览模态框
  const [currentVideo, setCurrentVideo] = useState(""); // 当前视频
  const [fileLoading, setFileLoading] = useState(false); // 文件加载状态
  const [previewFile, setPreviewFile] = useState<{ name: string; url: string | null } | null>(null); // 文件预览
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null); // 上传文件URL
  const uploadedFileUrlRef = useRef<string | null>(null); // 上传文件URL引用
  const [fileVisible, setFileVisible] = useState(false); // 文件预览模态框
  const [currentFile, setCurrentFile] = useState(""); // 当前文件
  const [chatStatus, setChatStatus] = useState<ChatStatus>(
    status as ChatStatus
  ); // 聊天状态
  const [messageRatings, setMessageRatings] = useState<Record<string, number>>(
    {}
  ); // 消息评分状态
  const fileInputRef = useRef<HTMLInputElement>(null); // 文件输入引用
  const [quickQuestions, setQuickQuestions] = useState<Bot.QuickQuestion[]>([]); // 快捷问题列表状态
  const [robotSettings, setRobotSettings] = useState<{
    robotCode?: string;
    openQuickQa?: boolean;
    switchChatEvaluate?: boolean;
    /** 客服正在输入提示；未返回时默认开启 */
    switchTypingHint?: boolean;
    agentAvatarUrl?: string;
  }>({}); // 机器人设置状态

  // 智能压缩器实例
  const smartCompressor = useMemo(
    () =>
      new SmartCompressor({
        enableProgressLog: true, // 启用详细日志
        forceMethod: "parameter", // 🔧 强制压缩方法: 'auto'(智能) | 'ffmpeg' | 'image' | 'parameter' | 'webcodecs' | 'canvas'
      }),
    []
  );

  return {
    // 基础状态
    content,
    setContent,
    messages,
    setMessages,
    previewVisible,
    setPreviewVisible,
    previewImage,
    setPreviewImage,
    csUuidRef,
    imageLoading,
    setImageLoading,
    previewBase64,
    setPreviewBase64,
    uploadedImageUrl,
    setUploadedImageUrl,
    uploadedImageUrlRef,
    videoLoading,
    setVideoLoading,
    previewVideo,
    setPreviewVideo,
    uploadedVideoUrl,
    setUploadedVideoUrl,
    uploadedVideoUrlRef,
    videoVisible,
    setVideoVisible,
    currentVideo,
    setCurrentVideo,
    fileLoading,
    setFileLoading,
    previewFile,
    setPreviewFile,
    uploadedFileUrl,
    setUploadedFileUrl,
    uploadedFileUrlRef,
    fileVisible,
    setFileVisible,
    currentFile,
    setCurrentFile,
    chatStatus,
    setChatStatus,
    messageRatings,
    setMessageRatings,
    fileInputRef,
    quickQuestions,
    setQuickQuestions,
    robotSettings,
    setRobotSettings,
    smartCompressor,
  };
};

